import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useParams } from 'react-router-dom';

import { getBlogPostBySlug } from './blogData';
import { usePublicSitePath } from './publicSitePaths';
import { PublicLanguage } from './types';
import PublicChatWidget from '../../components/public/PublicChatWidget';

interface BlogPostPageProps {
  language: PublicLanguage;
}

const BLOG_POST_UI: Record<
  PublicLanguage,
  {
    backToBlog: string;
    readTime: string;
    by: string;
  }
> = {
  en: {
    backToBlog: 'Back to Blog',
    readTime: 'min read',
    by: 'By',
  },
  es: {
    backToBlog: 'Volver al Blog',
    readTime: 'min de lectura',
    by: 'Por',
  },
};

const BlogPostPage: React.FC<BlogPostPageProps> = ({ language }) => {
  const { slug } = useParams();
  const ui = BLOG_POST_UI[language];
  const publicSitePath = usePublicSitePath();
  const [chatOpen, setChatOpen] = React.useState(false);

  if (!slug) {
    return <Navigate to={publicSitePath('/blog')} replace />;
  }

  const post = getBlogPostBySlug(slug);
  if (!post) {
    return <Navigate to={publicSitePath('/blog')} replace />;
  }

  const title = post.title[language];
  const description = post.excerpt[language];

  return (
    <>
      <Helmet>
        <title>{`${title} | UAB Blog`}</title>
        <meta name="description" content={description} />
      </Helmet>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <Link
          to={publicSitePath('/blog')}
          className="inline-flex items-center text-sm font-semibold text-blue-700 hover:text-blue-800"
        >
          {ui.backToBlog}
        </Link>

        <article className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <img src={post.imageUrl} alt={title} className="h-72 w-full object-cover" />

          <div className="p-6 md:p-8">
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-blue-700">
              <span className="rounded-full bg-blue-100 px-2.5 py-1">{post.category}</span>
              <span className="text-slate-500">{post.publishedAt}</span>
              <span className="text-slate-500">
                {post.readTimeMinutes} {ui.readTime}
              </span>
            </div>

            <h1 className="mt-3 text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
              {title}
            </h1>
            <p className="mt-3 text-slate-600">
              {ui.by} {post.author}
            </p>
            <p className="mt-4 text-lg text-slate-700">{description}</p>

            <div className="mt-6 flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600"
                >
                  #{tag.replace(/\s+/g, '-')}
                </span>
              ))}
            </div>

            <div className="mt-8 space-y-8">
              {post.sections[language].map((section) => (
                <section key={section.heading}>
                  <h2 className="text-2xl font-bold text-slate-900">{section.heading}</h2>
                  {section.imageUrl && (
                    <img
                      src={section.imageUrl}
                      alt={section.heading}
                      className="mt-4 w-full rounded-xl object-cover h-48 md:h-64"
                      loading="lazy"
                    />
                  )}
                  <div className="mt-3 space-y-3 text-slate-700 leading-7">
                    {section.paragraphs.map((paragraph, i) => (
                      <p key={i}>{paragraph}</p>
                    ))}
                  </div>
                  {section.bullets && section.bullets.length > 0 && (
                    <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-700">
                      {section.bullets.map((bullet, i) => (
                        <li key={i}>{bullet}</li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>

            {/* Inline chat prompt */}
            <div className="mt-10 rounded-xl border border-blue-200 bg-blue-50 p-5">
              <p className="text-sm font-semibold text-blue-800">
                {language === 'es'
                  ? '\u00bfTienes preguntas sobre lo que leiste?'
                  : 'Have questions about what you just read?'}
              </p>
              <p className="mt-1 text-sm text-blue-700">
                {language === 'es'
                  ? 'Nuestro asistente puede ayudarte ahora mismo.'
                  : 'Our assistant can help you right now.'}
              </p>
              <button
                onClick={() => setChatOpen(true)}
                className="mt-3 inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                {language === 'es' ? 'Preguntar al asistente \u2192' : 'Ask our assistant \u2192'}
              </button>
            </div>
            <PublicChatWidget
              isOpen={chatOpen}
              onClose={() => setChatOpen(false)}
              language={language}
            />
          </div>
        </article>
      </main>
    </>
  );
};

export default BlogPostPage;
